export type EvidenceFinding = {
  title: string;
  tag?: string;
  summary?: string;
  observations: string[];
  explanation: string;
};

export type EvidenceBlock = {
  intro: string;
  findings: EvidenceFinding[];
  unreadableLines: number;
};

type DraftFinding = EvidenceFinding & { explanationLines: string[] };

const HEADING = /^##(?!#)\s*(.*)$/;
const PROPERTY = /^(tag|summary):\s*(.*)$/i;
const OBSERVATION_PREFIX = 'observations/';
const OBSERVATION_NAME = /^observations\/[A-Za-z0-9_-]+$/;

const toFinding = ({
  explanationLines,
  ...finding
}: DraftFinding): EvidenceFinding => ({
  ...finding,
  explanation: explanationLines.join('\n').trim(),
});

export const parseEvidenceBlock = (content: string): EvidenceBlock => {
  const introLines: string[] = [];
  const drafts: DraftFinding[] = [];
  let current: DraftFinding | undefined;
  let unreadableLines = 0;

  for (const rawLine of content.split('\n')) {
    const line = rawLine.trim();
    const heading = HEADING.exec(line);
    if (heading) {
      const title = heading[1].trim();
      if (!title) {
        unreadableLines += 1;
        current = undefined;
        continue;
      }
      current = {
        title,
        observations: [],
        explanation: '',
        explanationLines: [],
      };
      drafts.push(current);
      continue;
    }

    if (line.startsWith(OBSERVATION_PREFIX)) {
      if (!current || !OBSERVATION_NAME.test(line)) {
        unreadableLines += 1;
      } else if (!current.observations.includes(line)) {
        current.observations.push(line);
      }
      continue;
    }

    if (!current) {
      introLines.push(rawLine);
      continue;
    }

    const property = PROPERTY.exec(line);
    if (property) {
      const key = property[1].toLowerCase() as 'tag' | 'summary';
      const value = property[2].trim();
      if (value && current[key] === undefined) current[key] = value;
      continue;
    }

    current.explanationLines.push(rawLine);
  }

  const findings: EvidenceFinding[] = [];
  for (const draft of drafts) {
    if (draft.observations.length === 0) {
      unreadableLines += 1;
    } else {
      findings.push(toFinding(draft));
    }
  }

  return {
    intro: introLines.join('\n').trim(),
    findings,
    unreadableLines,
  };
};

export const hasEvidenceFindings = (content: string): boolean =>
  parseEvidenceBlock(content).findings.length > 0;
